/**
 * PharmacyPage.tsx — the /pharmacy dispensing console (PHARMACY-gated).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: pharmacy dispensing lane
 * (2026-07-29).
 *
 * WHY it looks exactly like the other consoles: this is the SAME shell the admin
 * console and the clinician workspace mount — `ui/SideNav`'s dark green rail
 * inside `SideNavLayout`, with `ui/StaffMasthead`'s white top-bar block (logo,
 * vertical rule, workspace name as the page's one `<h1>`, per-section breadcrumb
 * under it) and the account pill on the right. Not one pixel of it is
 * re-derived here; a pharmacy-specific look would be a second design system to
 * maintain and would read to the CTO as a different product.
 *
 * THE RAIL HAS EXACTLY TWO ITEMS. Dispensing queue and My activity. There is no
 * plans screen, no pricing, no patient list and no money anywhere on this
 * console — not hidden, not permission-gated, ABSENT. The shapes the console
 * reads carry no money field at all (api/types.ts), so there is nothing for a
 * future component to accidentally render.
 *
 * SECTION STATE rides `?tab=` exactly like the other two consoles, so a
 * pharmacist can bookmark or share a link to either section (an unknown value
 * falls back to the queue rather than rendering a blank pane). No clinic waffle
 * switcher: the queue is ONE list tagged by originating clinic, which is what
 * makes a second clinic need no new screen.
 */
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { IconActivity, IconDispensing } from '../ui/icons';
import { SideNav, SideNavLayout, type SideNavItem } from '../ui/SideNav';
import { StaffMasthead } from '../ui/StaffMasthead';
import { DispensingQueue } from './DispensingQueue';
import { MyActivity } from './MyActivity';
import { PharmacyAccountMenu } from './PharmacyAccountMenu';
import { usePharmacySession } from './usePharmacySession';

type Tab = 'queue' | 'activity';

/** Icons are decorative (outline-only `currentColor` SVGs) — the label carries
 * the accessible name, the same rule the other two rails follow. */
const TABS: Array<SideNavItem<Tab>> = [
  { key: 'queue', label: 'Dispensing queue', icon: <IconDispensing /> },
  { key: 'activity', label: 'My activity', icon: <IconActivity /> },
];

function isTab(value: string | null): value is Tab {
  return TABS.some((t) => t.key === value);
}

export function PharmacyPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { session, signOut } = usePharmacySession();
  const tabParam = searchParams.get('tab');
  const tab: Tab = isTab(tabParam) ? tabParam : 'queue';
  const focusOrderCaseId = searchParams.get('order');
  const focusOrderRef = searchParams.get('orderRef');

  const clearOrderDeepLink = useCallback(() => {
    if (!searchParams.get('order')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('order');
    next.delete('orderRef');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const openOrderFromActivity = useCallback(
    (orderCaseId: string, orderRef: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'queue');
      next.set('order', orderCaseId);
      next.set('orderRef', orderRef);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  function selectTab(key: Tab) {
    // Copy the existing params so anything else on the URL survives a section
    // switch (the same contract the admin/clinician rails keep).
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  }

  const sectionLabel = TABS.find((t) => t.key === tab)?.label ?? 'Dispensing queue';

  return (
    <SideNavLayout
      nav={<SideNav label="Pharmacy sections" items={TABS} activeKey={tab} onSelect={selectTab} />}
      masthead={
        <StaffMasthead
          workspace="Pharmacy console"
          section={sectionLabel}
          homeTo="/pharmacy"
          homeLabel="GP Service — pharmacy home"
        />
      }
      // The gate guarantees a session; this stays defensive so a session cleared
      // mid-render never crashes the shell on the way to the redirect.
      account={session ? <PharmacyAccountMenu session={session} onSignOut={signOut} /> : null}
    >
      {tab === 'queue' ? (
        <DispensingQueue
          focusOrderCaseId={focusOrderCaseId}
          focusOrderRef={focusOrderRef}
          onFocusConsumed={clearOrderDeepLink}
        />
      ) : null}
      {tab === 'activity' ? <MyActivity onOpenOrder={openOrderFromActivity} /> : null}
    </SideNavLayout>
  );
}
